import {
  type DependencyList,
  type RefObject,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import {
  selectOrganizationId,
  useGetTourFramesDraftQuery,
  useGetTourFramesQuery,
  useGetTourQuery,
} from '@carboncopyco/data';
import type { Tour, TourAspectRatio, TourFrame } from '@carboncopyco/types';
import { ColorHex, LoadingDots } from '@carboncopyco/ui';
import { scopedLogger, Scope } from '@carboncopyco/logger';
import { useBoolean } from 'ahooks';
import classNames from 'classnames';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useScaleDownToWindow } from '../../hooks';
import { usePlayableFrames } from '../../hooks/replayer';
import { usePageEvent } from '../../hooks/usePageEvent';
import { useTourEvents } from '../../hooks/useTourEvents';
import { isValidAspectRatio } from '../../utils/replayUtils';
import { CoverImageLoader } from './../../components/CoverImageLoader';
import { ToursReplayerPreview } from './ToursReplayerPreview';
import { ToursReplayerPreviewWrapper } from './ToursReplayerPreviewWrapper';

const tourEngineLogger = scopedLogger(Scope.TOUR_ENGINE);

export type TourPreviewPageProps = {
  uuid: string;
  mimesisStepEngineUrl: string;
  fileServiceDynamicLink: string;
  isDebugMode: boolean;
  tabId?: number;
  activeTabId?: number;
  rootElement?: RefObject<HTMLDivElement>;
  tourFramesData?: TourFrame[];
  tourData?: Tour;
  initFrameDeps?: DependencyList;
  showWithoutDrafts?: boolean;
  onPlayStep?: (stepData: {
    stepId: number | string;
    stepIndex: number;
    frameIndex: number;
    completed: boolean;
    eventType: string;
  }) => void;
};

export const TourPreviewPage = ({
  uuid,
  mimesisStepEngineUrl,
  fileServiceDynamicLink,
  isDebugMode,
  tabId,
  activeTabId,
  rootElement,
  tourFramesData,
  tourData,
  initFrameDeps,
  showWithoutDrafts = false,
  onPlayStep,
}: TourPreviewPageProps) => {
  const flags = useFlags();
  const organizationId = useSelector(selectOrganizationId);
  const [initialLoad, setInitialLoad] = useBoolean(true);
  const { tourId, workspaceId } = useParams();

  const { data } = useGetTourQuery(
    { organizationId, workspaceId, id: Number(tourId) },
    {
      skip: !tourId || !!tourData,
      refetchOnMountOrArgChange: true,
    }
  );

  const tour = tourData || data;

  const { data: tourFramesDraft } = useGetTourFramesDraftQuery(
    {
      organizationId,
      id: Number(tourId),
    },
    {
      skip: !tour || tour?.isDeleted || !!tourFramesData || showWithoutDrafts,
      refetchOnMountOrArgChange: true,
    }
  );

  const { data: tourFrames } = useGetTourFramesQuery(
    {
      organizationId,
      id: Number(tourId),
      isDeleted: tour?.isDeleted,
    },
    {
      skip:
        (!tour || !tour?.isDeleted || !!tourFramesData) && !showWithoutDrafts,
      refetchOnMountOrArgChange: true,
    }
  );

  const frames = tourFramesData || tourFramesDraft || tourFrames;

  const { tourEvents } = useTourEvents(tour, frames);

  usePageEvent();

  useEffect(() => {
    if (tourEvents?.length > 2 && frames?.length > 0 && initialLoad) {
      setInitialLoad.setFalse();
    }
  }, [frames, initialLoad, setInitialLoad, tourEvents]);

  useEffect(() => {
    tourEngineLogger.setContextProperty('tourId', tourId);
  }, []);

  const tourAspectRatio: TourAspectRatio = useMemo(() => {
    return tour?.draftSettings?.responsiveness
      ? 'responsive'
      : 'fixedWidthAndHeight';
  }, [tour?.draftSettings?.responsiveness]);

  const aspectRatio: TourAspectRatio = useMemo(() => {
    if (
      tour?.draftSettings?.screenResolution?.type &&
      tour?.draftSettings?.screenResolution?.type !== 'responsive'
    ) {
      return 'fixedWidthAndHeight';
    }
    return isValidAspectRatio(tourAspectRatio) ? tourAspectRatio : 'responsive';
  }, [tourAspectRatio, tour?.draftSettings?.screenResolution]);

  const playableFrames = usePlayableFrames(uuid, frames, true);
  const loaderWrapperRef = useRef<HTMLDivElement>(null);
  const previewContainerRef = useRef<HTMLDivElement>(null);

  useScaleDownToWindow({
    aspectRatio,
    containerRef: previewContainerRef,
    playerIframe: document.createElement('iframe'),
    targetElement: loaderWrapperRef.current,
    isAnyFrameScreenshot: false,
    initialSize: {
      width: tour?.draftSettings?.screenResolution?.width,
      height: tour?.draftSettings?.screenResolution?.height,
    },
  });

  return (
    <ToursReplayerPreviewWrapper
      draftSettings={tour?.draftSettings}
      fileServiceApiToken={tour?.fileServiceToken}
      previewContainerRef={previewContainerRef}
    >
      {tour && frames?.length && tourEvents?.length > 2 ? (
        <ToursReplayerPreview
          onPlayStep={onPlayStep}
          uuid={uuid}
          frames={frames}
          events={tourEvents}
          tourSettings={tour.isDeleted ? tour.tourSettings : tour.draftSettings}
          fileServiceApiToken={tour?.fileServiceToken}
          tourAspectRatio={tourAspectRatio}
          fileServiceDynamicLink={fileServiceDynamicLink}
          mimesisStepEngineUrl={mimesisStepEngineUrl}
          isDebugMode={isDebugMode}
          tabId={tabId}
          activeTabId={activeTabId}
          rootElement={rootElement}
          initFrameDeps={initFrameDeps}
        />
      ) : (
        <div
          ref={loaderWrapperRef}
          className={classNames(
            'flex self-center justify-center items-center',
            {
              'w-full h-full': tourAspectRatio === 'responsive',
            }
          )}
        >
          {flags.toursCoverImageLoader &&
          aspectRatio === 'fixedWidthAndHeight' &&
          playableFrames[0]?.frameScreenshotUrl ? (
            <CoverImageLoader
              url={playableFrames[0].frameScreenshotUrl}
              fileServiceApiToken={tour?.fileServiceToken}
            />
          ) : (
            <LoadingDots color={ColorHex.natural600} />
          )}
        </div>
      )}
    </ToursReplayerPreviewWrapper>
  );
};
